import { onMounted, onUnmounted } from 'vue';
import { useSettings } from '@/config/settings';

export const fetchLatestCommitId = async (): Promise<string | null> => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { commitId?: string };
    return json.commitId ?? null;
  } catch {
    return null;
  }
};

// 埋め込みcommitIdが空の場合は不整合とみなさない。
// 本番(Amplify Hosting)ではAWS_COMMIT_IDが必ず提供されるため通常は空にならないが、
// Amplify以外でビルドした場合や環境変数の設定漏れ時の安全装置として比較をスキップする。
export const isCommitIdMismatch = (
  current: string,
  latest: string | null
): boolean => {
  if (!current || !latest) return false;
  return current !== latest;
};

export const isForceCheckEnabled = (search: string): boolean => {
  return new URLSearchParams(search).has('force-version-check');
};

// 無限リロード防止用の最終リロード時刻保存キー。
// /version.json のキャッシュヘッダ設定漏れや index.html がキャッシュされたままなど、
// リロード後も埋め込みcommitIdが更新されない異常状態に陥った場合の安全装置として利用する。
const LAST_RELOAD_AT_KEY = '__srest_version_check_last_reload_at';
const MIN_RELOAD_INTERVAL_MS = 60_000;

const shouldSuppressReload = (now: number): boolean => {
  try {
    const lastReloadAt = Number(
      sessionStorage.getItem(LAST_RELOAD_AT_KEY) || 0
    );
    return now - lastReloadAt < MIN_RELOAD_INTERVAL_MS;
  } catch {
    return false;
  }
};

const markReloaded = (now: number): void => {
  try {
    sessionStorage.setItem(LAST_RELOAD_AT_KEY, String(now));
  } catch {
    // sessionStorage 利用不可（プライベートモード等）でも本機能は継続する。
  }
};

/**
 * デプロイ済みのフロントエンドが最新かをチェックする。
 * ビルド時に埋め込まれた commitId と、デプロイ時に生成された /version.json の
 * commitId を比較し、不一致なら画面を強制リロードする。
 *
 * ローカル環境では settings.versionCheck.enabled が false のため無効。
 * URLクエリ `?force-version-check=true` を付与すると localhost でも強制的に動作する。
 */
export const useVersionCheck = () => {
  const { settings } = useSettings();
  let reloading = false;

  const enabled =
    settings.versionCheck.enabled ||
    (typeof window !== 'undefined' &&
      isForceCheckEnabled(window.location.search));

  const check = async () => {
    if (reloading) return;
    const latest = await fetchLatestCommitId();
    if (!isCommitIdMismatch(settings.commitId, latest)) return;

    const now = Date.now();
    if (shouldSuppressReload(now)) return;

    reloading = true;
    markReloaded(now);
    window.location.reload();
  };

  const handler = () => {
    if (document.visibilityState === 'visible') {
      check();
    }
  };

  onMounted(() => {
    if (!enabled) return;
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', check);
    // 初回マウント時にもチェックする。
    // 本番ではユーザーが取得した直後のJSと/version.jsonは一致しているはずなので
    // 不一致が発生するケースはほぼ無く害がない一方、強制有効化での動作確認時に
    // タブ切り替えを待たずに即座にリロードを確認できる利点がある。
    check();
  });

  onUnmounted(() => {
    if (!enabled) return;
    document.removeEventListener('visibilitychange', handler);
    window.removeEventListener('focus', check);
  });
};
